import { cache } from "../../persistence/cache";
import { layoutTokenStore, token } from "../../store";
import { memoryCache } from "../../persistence/memoryCache";
import { sections } from "../../../constants";
import shuffle from "../../../utils/shuffleArray";

const LAYOUTS = "layouts";
const CHANNELEVENTS = "channelevents";

async function generateCacheKey(section, lang) {
  const isLoggedIn = await token.getToken.call(this);
  return isLoggedIn ? `${section}-${lang}-logged` : `${section}-${lang}`;
}

async function generateChannelCacheKey(lang, channels, tsstart) {
  const isLoggedIn = await token.getToken.call(this);
  const channelKey = "channelevents";
  return isLoggedIn
    ? `${channelKey}-${channels}-${lang}-${tsstart}-logged`
    : `${channelKey}-${channels}-${lang}-${tsstart}`;
}

/**
 * Receives a response from the layouts middleware and, depending on a boolean config property, looks for the modules
 * that are isDynamic: false and have already an array of titles injected. If that's the case, it will shuffle this
 * array of titles to deliver it all mixed everytime the client requests a layout.
 * @param {Function} originalFunction - mediaManager.getLayout method that will be wrapped with the subsequent cache
 * storage logic. In case there was a cached version for the layout object that was requested, the shuffling will act
 * over this cached data.
 * @returns {Object} A new getLayout function that will deliver the response value with the shuffling applied depending
 * on a config variable and the modules conditions (having titles array and being static).
 * @public
 */
export function shuffleLayoutStaticModules(originalFunction) {
  return async function(section, config) {
    const layout = await originalFunction.call(this, section, config);
    if (this.shuffleLayoutStaticModules) {
      const staticModulesWithTitles = layout.modules.filter(
        module => module.isDynamic === false && module.titles && module.titles.length > 0
      );
      for (let i = 0; i < staticModulesWithTitles.length; i++) {
        shuffle(staticModulesWithTitles[i].titles);
      }
    }
    return layout;
  };
}

/**
 * Receives a response from the layouts middleware and adds the modules present on it to the list of modules that were
 * previously cached locally. The approach is to check if the modules coming from the MW contain an array of titles and
 * in case of having, the function considers that module needs to be updated so puts it in the position of the currently
 * cached one.
 * @param {Object} layout An object containing a response from the layouts MW endpoint. It contains a property named
 * total with a numeric value specifying the total amount of modules in the layout and a list of modules that have been
 * requested to the endpoint.
 * @param {String} key  The key used to store the data corresponding to the layout section that needs to be updated
 * @param {Object} cacheConfig  Object that allows passing a custom cache implementation to get the data from. Currently
 * the different options are cache (IndexedDB) and memoryCache. The object expects 2 properties, the first one being
 * called implementation with a reference to the object providing access to the cache management functions and the
 * second one, which is optional, a configuration object to be used with the cache that will be used.
 * @return {Promise<*>} A promise that resolves to the merged layout object or to the layout object received as a
 * parameter if there is no cached counterpart.
 */
async function mergeReceivedLayoutWithCachedVersion(
  layout,
  key,
  cacheConfig = { implementation: cache, databaseConfig: { database: LAYOUTS } }
) {
  let cachedLayout = await cacheConfig.implementation.get(key, cacheConfig.databaseConfig);
  if (cachedLayout) {
    layout.modules.forEach(module => {
      let moduleIndex = cachedLayout.modules.findIndex(cachedModule => cachedModule.id === module.id);
      if (moduleIndex !== -1 && module && module.titles) {
        cachedLayout.modules[moduleIndex].titles = module.titles;
      }
    });
    return cachedLayout;
  } else {
    return layout;
  }
}

/**
 * Extends getLayout method and save the response modules to the SDK cache
 * @param {function} originalFunction mediaManager.getLayout method that will be wrapped with the subsequent cache
 * storage logic. In case there was a cached version for the layout object that was requested, this function will merge
 * both to ensure no data is lost. Given that the layout modules can be requested on demand, it can happen that on the
 * moment of having to cache a response, there is already data for other modules that were not requested this time. Due
 * to this, the caching will avoid blindly overwriting the cached content and will only add the new modules to the
 * existing cached data.
 * @returns {object} A new getLayout function that will store the response value in local cache after making the call to
 * the original function.
 * @private
 */
export function toLayoutCache(originalFunction) {
  return async function(section, config = {}) {
    const layout = await originalFunction.call(this, section, config);
    if (this.localCache && this.localCache.layouts) {
      const key = await generateCacheKey.call(this, `${section}-modules`, this.lang);
      const layoutToCache = await mergeReceivedLayoutWithCachedVersion(layout, key);
      if (layoutToCache.total > 0) {
        cache.set(key, layoutToCache, {
          database: LAYOUTS,
          expiresIn: this.layoutCacheTime
        });
      }
      return layoutToCache;
    }
    return layout;
  };
}

export function toLayoutMemoryCache(originalFunction) {
  // FIXME This function is almost the same as toLayoutCache
  return async function(section, config = {}) {
    const layout = await originalFunction.call(this, section, config);
    if (this.localCache && this.localCache.memoryCache) {
      const key = await generateCacheKey.call(this, `${section}-modules`, this.lang);
      const layoutToCache = await mergeReceivedLayoutWithCachedVersion(layout, key, { implementation: memoryCache });
      if (layoutToCache.total > 0) {
        memoryCache.set(key, layoutToCache, {
          callback: () => layoutTokenStore.clear()
        });
      }
      return layoutToCache;
    }
    return layout;
  };
}

export function toChannelEventsCache(originalFunction) {
  return async function(data) {
    const channelevents = await originalFunction.call(this, data);
    const key = await generateChannelCacheKey.call(this, data.lang, data.channels, data.ts_start);
    if (this.localCache && this.localCache.channelevents && channelevents.total > 0) {
      cache.set(key, channelevents, {
        database: CHANNELEVENTS,
        expiresIn: this.channelEventsCacheTime
      });
    }
    return channelevents;
  };
}

export function toChannelEventsMemoryCache(originalFunction) {
  return async function(data) {
    const channelevents = await originalFunction.call(this, data);
    if (this.localCache && this.localCache.memoryCache && channelevents.total > 0) {
      const key = await generateChannelCacheKey.call(this, data.lang, data.channels, data.ts_start);
      memoryCache.set(key, channelevents, {
        // expiresIn: this.channelEventsCacheTime
      });
    }
    return channelevents;
  };
}

/**
 * Get section and layout response to store the x-query and x-token in the layoutToken store
 *
 * @param {string} section layout section like home/movies/series Etc
 * @param {object} response layout reponse object
 */
export async function toLayoutTokenStore(section, response) {
  if (!!response && !!response.layout && !!response.layout["x-query"] && !!response.layout["x-token"]) {
    const xQuery = response.layout["x-query"];
    const xToken = response.layout["x-token"];
    layoutTokenStore.update(section, { xQuery, xToken });
  }
}

/**
 * Extends getLayout method adding caching capabilities to it. This decorator will check if the data requested to the
 * original getLayout function is present in the local cache and will avoid the call in case this is the case. There are
 * some exceptions that will bypass the local cache even if the requested data is present there. These exceptions
 * correspond to config.avoidCache and config.avoidCacheFor options that can be passed to
 * [SDK.mediaManager.getLayout()]{@link module:mediaManager~getLayout}
 * @param {function} originalFunction mediaManager.getLayout method that will be wrapped with the previous cache
 * storage logic. In case there was a cached version for the layout object that was requested, this function will return
 * the cached data to the caller. Given that the layout modules can be requested on demand, it can happen that on the
 * moment of having to make a request to getLayout, there is some data that is cached but some data that is not. In that
 * case this decorator will manipulate the config object that gets passed to getLayout in order to make it only call
 * the data that is not cached.
 * @returns {object} A new getLayout function that will check the cache before making the call to the original function.
 * @private
 */
export function fromLayoutCache(originalFunction) {
  return async function(section, config) {
    if (this.localCache && this.localCache.layouts && !config.avoidCache) {
      const key = await generateCacheKey.call(this, `${section}-modules`, this.lang);
      let cachedLayout = await cache.get(key, { database: LAYOUTS });
      if (cachedLayout) {
        config = overrideRequestBasedOnCacheConfig(config, cachedLayout);
        if (config.modulesByPosition && config.modulesByPosition.length > 0) {
          return await originalFunction.call(this, section, config);
        } else {
          return cachedLayout;
        }
      } else {
        return await originalFunction.call(this, section, config);
      }
    } else {
      return await originalFunction.call(this, section, config);
    }
  };
}

export function fromLayoutMemoryCache(originalFunction) {
  // FIXME This function is almost the same as fromLayoutCache
  return async function(section, config) {
    if (this.localCache && this.localCache.memoryCache && !config.avoidCache) {
      const key = await generateCacheKey.call(this, `${section}-modules`, this.lang);
      let cachedLayout = await memoryCache.get(key);
      if (cachedLayout) {
        config = overrideRequestBasedOnCacheConfig(config, cachedLayout);
        if (config.modulesByPosition && config.modulesByPosition.length > 0) {
          return await originalFunction.call(this, section, config);
        } else {
          return cachedLayout;
        }
      } else {
        return await originalFunction.call(this, section, config);
      }
    } else {
      return await originalFunction.call(this, section, config);
    }
  };
}

export function fromChannelEventsCache(originalFunction) {
  return async function(data) {
    if (this.localCache && this.localCache.channelevents) {
      const key = await generateChannelCacheKey.call(this, data.lang, data.channels, data.ts_start);
      let channelEventsList = await cache.get(key, {
        database: CHANNELEVENTS
      });
      if (!channelEventsList) {
        return await originalFunction.call(this, data);
      }
      return channelEventsList;
    } else {
      return await originalFunction.call(this, data);
    }
  };
}

export function fromChannelEventsMemoryCache(originalFunction) {
  return async function(data) {
    if (this.localCache && this.localCache.memoryCache) {
      const key = await generateChannelCacheKey.call(this, data.lang, data.channels, data.ts_start);
      let channelEventsList = await memoryCache.get(key);
      if (!channelEventsList) {
        return await originalFunction.call(this, data);
      }
      return channelEventsList;
    } else {
      return await originalFunction.call(this, data);
    }
  };
}

function overrideRequestBasedOnCacheConfig(config, cachedLayout) {
  if (config.initialModule !== undefined && config.finalModule !== undefined) {
    config = calculateItemsToRequestForRangeBasedConfig(config, cachedLayout);
  } else if (config.modulesByPosition) {
    config = calculateItemsToRequestForPositionBasedConfig(config, cachedLayout);
  } else if (config.modulesById) {
    config = calculateItemsToRequestForIdBasedConfig(config, cachedLayout);
  }
  return config;
}

function calculateItemsToRequestForRangeBasedConfig(config, cachedLayout) {
  const itemsToRequest = [];
  for (let i = config.initialModule; i <= config.finalModule && i < cachedLayout.total; i++) {
    if (!isModuleCached(cachedLayout.modules[i]) || (config.avoidCacheFor && config.avoidCacheFor.includes(i))) {
      itemsToRequest.push(i);
    }
  }
  delete config.initialModule;
  delete config.finalModule;
  config.modulesByPosition = itemsToRequest;
  return config;
}

function calculateItemsToRequestForPositionBasedConfig(config, cachedLayout) {
  config.modulesByPosition = config.modulesByPosition.filter(position => {
    return (
      !isModuleCached(cachedLayout.modules[position]) ||
      (config.avoidCacheFor && config.avoidCacheFor.includes(position))
    );
  });
  return config;
}

function calculateItemsToRequestForIdBasedConfig(config, cachedLayout) {
  config.modulesByPosition = config.modulesById.filter(id => {
    const module = cachedLayout.modules.find(cachedModule => cachedModule.id === id);
    return !isModuleCached(module) || (config.avoidCacheFor && config.avoidCacheFor.includes(id));
  });
  delete config.modulesById;
  return config;
}

function isModuleCached(module) {
  return !!(module && module.titles);
}

/**
 * Removes the cached version of the layout data for the given section apart from calling the original function that
 * decorates. The final effect will be that the original function will be called but in addition the locally cached
 * section's layout will be cleared.
 * @param {String} section  The layout section to be removed when calling the wrapped function.
 * @param originalFunction  The function this decorator is extending or decorating.
 * @returns {function}      A new function having the original logic plus the cache clearing addition.
 * @private
 */
export function clearLayoutOnWatchlistUpdate(section, originalFunction) {
  return function(...params) {
    if (this.localCache) {
      const key = `${section}-modules-${this.lang}-logged`;
      if (this.localCache.layouts || this.localCache.memoryCache) {
        if (section === sections.ALL) {
          cache.clear({
            database: LAYOUTS
          });
          if (this.localCache.memoryCache) {
            memoryCache.remove(/modules/);
          }
        } else {
          cache.remove(key, {
            database: LAYOUTS
          });
          if (this.localCache.memoryCache) {
            memoryCache.remove(key);
          }
        }
      }
      layoutTokenStore.clear();
      return originalFunction.apply(this, params);
    }
  };
}

export function clearLayoutCache(section = sections.ALL) {
  if (this.localCache) {
    const key = `${section}-modules-${this.lang}-logged`;
    if (this.localCache.layouts || this.localCache.memoryCache) {
      if (section === sections.ALL) {
        cache.clear({
          database: LAYOUTS
        });
        if (this.localCache.memoryCache) {
          memoryCache.remove(/modules/);
        }
      } else {
        cache.remove(key, {
          database: LAYOUTS
        });
        if (this.localCache.memoryCache) {
          memoryCache.remove(key);
        }
      }
    }
    layoutTokenStore.clear();
  }
}
